import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import translations from "./translations";
import { 
  ChevronRight, ArrowRight, Download, Mail, Phone, MapPin, Users, TrendingUp, Award, Briefcase,
  Globe, Zap, Palette, MessageSquare, Bot, Target, FileText, Building2, Percent, ExternalLink,
  Menu, X, Check, Layers, Megaphone, Code, Settings, PenTool, Clock, Shield, Lock, Eye, EyeOff,
  Trash2, RefreshCw, Filter, Instagram, Facebook, Linkedin, CalendarDays, Quote, Copy, 
  FileImage, Presentation, Mail as MailIcon, Share2, FolderOpen, ChevronLeft
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Language Context
const LanguageContext = createContext();
export const useLanguage = () => useContext(LanguageContext);

// Brand Assets
const RUMBO_LOGO = "https://customer-assets.emergentagent.com/job_47805d82-a329-4ae4-a2e5-fb1c63a16fe0/artifacts/jg5uqjpm_RumboGroup-Logo-Official.jpg";

const IMAGES = {
  heroGraphicWorks: "https://static.prod-images.emergentagent.com/jobs/47805d82-a329-4ae4-a2e5-fb1c63a16fe0/images/e9ad7b864694e03fa198cb5b0d59f8941c1a9f17f0caa648949f693093c95af1.png",
  heroRumboGroup: "https://static.prod-images.emergentagent.com/jobs/47805d82-a329-4ae4-a2e5-fb1c63a16fe0/images/813219ff161d748532209d812992a29248c63540dc408528ab70ca0bf9cf2876.png",
  investorBg: "https://static.prod-images.emergentagent.com/jobs/47805d82-a329-4ae4-a2e5-fb1c63a16fe0/images/98a08a1c01c0d36439717c9627a87ad2c4c3f38db03ce93e474666bdd1c349f8.png",
  synergyBg: "https://static.prod-images.emergentagent.com/jobs/47805d82-a329-4ae4-a2e5-fb1c63a16fe0/images/578f083f7c85073c20c2239b099060649be7824618112aa47fb386c77be7ee8b.png"
};

// Portfolio Images
const portfolioItems = [
  { id: 1, category: "pop", titleEn: "Luxury Cosmetics Display", titleEs: "Exhibidor de Cosméticos de Lujo", client: "Estée Lauder", image: "https://images.unsplash.com/photo-1760621393386-3906922b0b78?w=800" },
  { id: 2, category: "pop", titleEn: "Retail Store Display", titleEs: "Exhibidor de Tienda", client: "JCPenney", image: "https://images.unsplash.com/photo-1768881187102-ca0131989c8a?w=800" },
  { id: 3, category: "pop", titleEn: "Premium Skincare Display", titleEs: "Exhibidor Premium de Skincare", client: "Clinique", image: "https://images.unsplash.com/photo-1760614034530-a0d34463e03d?w=800" },
  { id: 4, category: "vehicle", titleEn: "Fleet Branding", titleEs: "Rotulación de Flota", client: "Coca-Cola", image: "https://images.unsplash.com/photo-1568821552978-36c3fa12d402?w=800" },
  { id: 5, category: "vehicle", titleEn: "Commercial Vehicle Wrap", titleEs: "Rotulación Vehicular Comercial", client: "Local Business", image: "https://images.unsplash.com/photo-1573495407624-9c6cb5097ad1?w=800" },
  { id: 6, category: "vehicle", titleEn: "Delivery Van Graphics", titleEs: "Gráficos de Van de Entrega", client: "Distribution Co", image: "https://images.unsplash.com/photo-1573495407648-e62631194e2e?w=800" },
  { id: 7, category: "signage", titleEn: "Illuminated Channel Letters", titleEs: "Letras de Canal Iluminadas", client: "Retail Store", image: "https://images.unsplash.com/photo-1604066867775-43f48e3957d8?w=800" },
  { id: 8, category: "signage", titleEn: "Custom 3D Signage", titleEs: "Señalización 3D Personalizada", client: "Corporate Office", image: "https://images.unsplash.com/photo-1616037502269-72da0baf7d71?w=800" },
  { id: 9, category: "largeformat", titleEn: "Billboard Campaign", titleEs: "Campaña de Valla Publicitaria", client: "Entertainment", image: "https://images.unsplash.com/photo-1698307191710-57e006bdc5f5?w=800" },
  { id: 10, category: "largeformat", titleEn: "Building Banner", titleEs: "Banner de Edificio", client: "Real Estate", image: "https://images.unsplash.com/photo-1580670029149-5c00eec8bb66?w=800" },
  { id: 11, category: "branding", titleEn: "Storefront Design", titleEs: "Diseño de Fachada", client: "Fashion Boutique", image: "https://images.unsplash.com/photo-1769766408016-38b111720e3d?w=800" },
  { id: 12, category: "branding", titleEn: "Retail Interior Branding", titleEs: "Branding Interior Retail", client: "Macy's", image: "https://images.unsplash.com/photo-1760377294186-8bf28829a368?w=800" }
];

// Testimonials Data
const testimonials = [
  { id: 1, nameEn: "Marketing Director", nameEs: "Directora de Marketing", company: "Estée Lauder PR", quoteEn: "Graphic Works has been our trusted partner for over 15 years. Their POP displays consistently drive a 25-30% increase in product visibility and sales.", quoteEs: "Graphic Works ha sido nuestro socio de confianza por más de 15 años. Sus exhibidores POP consistentemente generan un aumento del 25-30% en visibilidad y ventas.", rating: 5 },
  { id: 2, nameEn: "Regional Manager", nameEs: "Gerente Regional", company: "JCPenney", quoteEn: "The quality of their retail displays and signage is unmatched in Puerto Rico. They understand luxury retail branding like no one else.", quoteEs: "La calidad de sus exhibidores y señalización es inigualable en Puerto Rico. Entienden el branding de retail de lujo como nadie.", rating: 5 },
  { id: 3, nameEn: "Store Operations", nameEs: "Operaciones de Tienda", company: "Clinique", quoteEn: "From concept to installation, Graphic Works delivers excellence. Their team's attention to detail ensures our brand is always represented perfectly.", quoteEs: "Desde el concepto hasta la instalación, Graphic Works entrega excelencia. La atención al detalle de su equipo asegura que nuestra marca siempre se represente perfectamente.", rating: 5 },
  { id: 4, nameEn: "Brand Manager", nameEs: "Gerente de Marca", company: "Macy's PR", quoteEn: "We've worked with many vendors, but Graphic Works stands out for their reliability, creativity, and commitment to deadlines.", quoteEs: "Hemos trabajado con muchos proveedores, pero Graphic Works destaca por su confiabilidad, creatividad y compromiso con los plazos.", rating: 5 },
  { id: 5, nameEn: "Visual Merchandising", nameEs: "Visual Merchandising", company: "TOUS", quoteEn: "Their vehicle wraps for our delivery fleet have turned our trucks into mobile billboards. The ROI has been incredible.", quoteEs: "Sus rotulaciones vehiculares para nuestra flota han convertido nuestros camiones en vallas móviles. El ROI ha sido increíble.", rating: 5 }
];

// Marketing Resources Data
const marketingResources = {
  business: [
    { id: 'bp-en', nameEn: "Business Plan (English)", nameEs: "Plan de Negocios (Inglés)", type: "PDF", size: "2.4 MB", url: "https://customer-assets.emergentagent.com/job_47805d82-a329-4ae4-a2e5-fb1c63a16fe0/artifacts/335su5ob_RumboGroup-BusinessPlan.pdf" },
    { id: 'bp-es', nameEn: "Business Plan (Spanish)", nameEs: "Plan de Negocios (Español)", type: "PDF", size: "2.1 MB", url: "https://customer-assets.emergentagent.com/job_47805d82-a329-4ae4-a2e5-fb1c63a16fe0/artifacts/03ya1gwz_RumboGroup-PlanDeNegocios-ES.pdf" }
  ],
  branding: [
    { id: 'logo', nameEn: "Logo & Brand Guidelines", nameEs: "Logo y Guías de Marca", type: "PDF", size: "1.8 MB", url: "https://customer-assets.emergentagent.com/job_47805d82-a329-4ae4-a2e5-fb1c63a16fe0/artifacts/da3alkj2_RumboGroup-Logo.pdf" },
    { id: 'logo-img', nameEn: "Official Logo (JPG)", nameEs: "Logo Oficial (JPG)", type: "JPG", size: "245 KB", url: RUMBO_LOGO }
  ],
  onepagers: [
    { id: 'op-gw', nameEn: "Graphic Works One-Pager", nameEs: "One-Pager Graphic Works", type: "Template", descEn: "33 years of visual excellence - services overview", descEs: "33 años de excelencia visual - resumen de servicios" },
    { id: 'op-rg', nameEn: "Rumbo Group One-Pager", nameEs: "One-Pager Rumbo Group", type: "Template", descEn: "AI-powered marketing agency overview", descEs: "Resumen de agencia de marketing con IA" },
    { id: 'op-inv', nameEn: "Investor Teaser", nameEs: "Teaser para Inversores", type: "Template", descEn: "Quick investment opportunity overview", descEs: "Resumen rápido de oportunidad de inversión" }
  ],
  email: [
    { id: 'em-intro', nameEn: "Introduction Email", nameEs: "Email de Introducción", type: "Template" },
    { id: 'em-followup', nameEn: "Follow-up Email", nameEs: "Email de Seguimiento", type: "Template" },
    { id: 'em-investor', nameEn: "Investor Outreach", nameEs: "Outreach a Inversores", type: "Template" },
    { id: 'em-partner', nameEn: "Partnership Proposal", nameEs: "Propuesta de Asociación", type: "Template" }
  ]
};

// Email Templates Content
const emailTemplates = {
  'em-intro': {
    en: { subject: "Introduction: Rumbo Group - Puerto Rico's First AI Marketing Agency", body: `Dear [Name],

I hope this email finds you well. My name is [Your Name], and I'm reaching out to introduce Rumbo Group, Puerto Rico's first AI-native marketing agency.

With 33 years of industry experience through our sister company Graphic Works, we've served prestigious brands like Estée Lauder, JCPenney, and Clinique. Now, we're bringing that expertise into the digital age with AI-powered marketing solutions.

What sets us apart:
• AI-powered marketing at SMB-friendly prices
• Bilingual (EN/ES) service for PR & LATAM markets
• 4% corporate tax rate under Puerto Rico's Act 60
• $3M+ revenue trajectory by Year 3

I'd love to schedule a brief call to discuss how we might collaborate. Would you have 15 minutes this week?

Best regards,
[Your Name]
Rumbo Group LLC
(787) 564-4035
graphicw1@gmail.com` },
    es: { subject: "Introducción: Rumbo Group - La Primera Agencia de Marketing IA de Puerto Rico", body: `Estimado/a [Nombre],

Espero que este correo le encuentre bien. Mi nombre es [Tu Nombre], y me comunico para presentarle Rumbo Group, la primera agencia de marketing nativa de IA en Puerto Rico.

Con 33 años de experiencia en la industria a través de nuestra empresa hermana Graphic Works, hemos atendido marcas prestigiosas como Estée Lauder, JCPenney y Clinique. Ahora, estamos llevando esa experiencia a la era digital con soluciones de marketing impulsadas por IA.

Lo que nos diferencia:
• Marketing impulsado por IA a precios accesibles para PYMES
• Servicio bilingüe (EN/ES) para PR y mercados LATAM
• 4% de impuesto corporativo bajo la Ley 60 de Puerto Rico
• Trayectoria de ingresos de $3M+ para el Año 3

Me encantaría programar una breve llamada para discutir cómo podríamos colaborar. ¿Tendría 15 minutos esta semana?

Saludos cordiales,
[Tu Nombre]
Rumbo Group LLC
(787) 564-4035
graphicw1@gmail.com` }
  },
  'em-followup': {
    en: { subject: "Following Up: Rumbo Group Partnership Opportunity", body: `Dear [Name],

I wanted to follow up on my previous email regarding Rumbo Group's services.

I understand you're busy, so I'll keep this brief. We've recently helped businesses like yours achieve:
• 40% increase in social media engagement
• 3x improvement in lead generation
• 25% reduction in marketing costs through AI automation

I've attached our one-pager for a quick overview of our capabilities.

Would you be open to a 10-minute call this week? I'm happy to work around your schedule.

Best regards,
[Your Name]
(787) 564-4035` },
    es: { subject: "Seguimiento: Oportunidad de Asociación con Rumbo Group", body: `Estimado/a [Nombre],

Quería dar seguimiento a mi correo anterior sobre los servicios de Rumbo Group.

Entiendo que está ocupado/a, así que seré breve. Recientemente hemos ayudado a negocios como el suyo a lograr:
• 40% de aumento en engagement en redes sociales
• 3x mejora en generación de leads
• 25% de reducción en costos de marketing mediante automatización con IA

Adjunto nuestro one-pager para un resumen rápido de nuestras capacidades.

¿Estaría abierto/a a una llamada de 10 minutos esta semana? Estoy feliz de ajustarme a su horario.

Saludos cordiales,
[Tu Nombre]
(787) 564-4035` }
  },
  'em-investor': {
    en: { subject: "Investment Opportunity: Puerto Rico's First AI Marketing Agency", body: `Dear [Investor Name],

I'm reaching out regarding an exciting investment opportunity in Puerto Rico's emerging AI marketing sector.

Rumbo Group is the first AI-native marketing agency in Puerto Rico, positioned to capture a rapidly growing market with significant advantages:

INVESTMENT HIGHLIGHTS:
• First-mover advantage in untapped PR market
• 4% corporate tax rate (Act 60)
• $3M+ revenue projection by Year 3
• 33-year track record via Graphic Works
• Proprietary AI platform (Digital Works AI)

We're currently seeking strategic partners who share our vision for AI-powered business growth in the Caribbean and LATAM markets.

I've attached our comprehensive business plan for your review. I would welcome the opportunity to discuss this further at your convenience.

Best regards,
[Your Name]
Founder, Rumbo Group LLC
(787) 564-4035` },
    es: { subject: "Oportunidad de Inversión: Primera Agencia de Marketing IA de Puerto Rico", body: `Estimado/a [Nombre del Inversor],

Me comunico respecto a una emocionante oportunidad de inversión en el emergente sector de marketing con IA de Puerto Rico.

Rumbo Group es la primera agencia de marketing nativa de IA en Puerto Rico, posicionada para capturar un mercado en rápido crecimiento con ventajas significativas:

PUNTOS DESTACADOS DE LA INVERSIÓN:
• Ventaja de primer movimiento en mercado PR sin explotar
• 4% de tasa de impuesto corporativo (Ley 60)
• Proyección de ingresos de $3M+ para el Año 3
• 33 años de trayectoria vía Graphic Works
• Plataforma IA propietaria (Digital Works AI)

Actualmente buscamos socios estratégicos que compartan nuestra visión para el crecimiento empresarial impulsado por IA en los mercados del Caribe y LATAM.

Adjunto nuestro plan de negocios completo para su revisión. Agradecería la oportunidad de discutir esto más a fondo cuando le sea conveniente.

Saludos cordiales,
[Tu Nombre]
Fundador, Rumbo Group LLC
(787) 564-4035` }
  },
  'em-partner': {
    en: { subject: "Strategic Partnership Proposal: Rumbo Group x [Company]", body: `Dear [Name],

I'm writing to propose a strategic partnership between [Their Company] and Rumbo Group.

WHO WE ARE:
Rumbo Group is Puerto Rico's first AI-native marketing agency, backed by 33 years of industry experience through Graphic Works. We serve clients like Estée Lauder, JCPenney, and Clinique.

PARTNERSHIP OPPORTUNITY:
We see tremendous synergy between our organizations. Specifically:
• [Customize based on their business]
• Joint marketing initiatives
• Co-branded solutions
• Revenue sharing opportunities

WHAT WE BRING:
• AI-powered marketing expertise
• Bilingual team (EN/ES)
• Established network in PR & Caribbean
• Proven track record with luxury brands

I'd love to explore how we can create mutual value. Are you available for a call this week?

Best regards,
[Your Name]
Rumbo Group LLC
(787) 564-4035` },
    es: { subject: "Propuesta de Asociación Estratégica: Rumbo Group x [Empresa]", body: `Estimado/a [Nombre],

Le escribo para proponer una asociación estratégica entre [Su Empresa] y Rumbo Group.

QUIÉNES SOMOS:
Rumbo Group es la primera agencia de marketing nativa de IA de Puerto Rico, respaldada por 33 años de experiencia en la industria a través de Graphic Works. Atendemos clientes como Estée Lauder, JCPenney y Clinique.

OPORTUNIDAD DE ASOCIACIÓN:
Vemos tremenda sinergia entre nuestras organizaciones. Específicamente:
• [Personalizar según su negocio]
• Iniciativas de marketing conjuntas
• Soluciones co-branded
• Oportunidades de revenue sharing

LO QUE APORTAMOS:
• Experiencia en marketing impulsado por IA
• Equipo bilingüe (EN/ES)
• Red establecida en PR y Caribe
• Trayectoria probada con marcas de lujo

Me encantaría explorar cómo podemos crear valor mutuo. ¿Está disponible para una llamada esta semana?

Saludos cordiales,
[Tu Nombre]
Rumbo Group LLC
(787) 564-4035` }
  }
};

// Revenue Data
const revenueData = [
  { month: 'M1', revenue: 4000 }, { month: 'M2', revenue: 8500 }, { month: 'M3', revenue: 14000 },
  { month: 'M4', revenue: 22000 }, { month: 'M5', revenue: 32000 }, { month: 'M6', revenue: 45000 },
  { month: 'M7', revenue: 52000 }, { month: 'M8', revenue: 60000 }, { month: 'M9', revenue: 68000 },
  { month: 'M10', revenue: 75000 }, { month: 'M11', revenue: 82000 }, { month: 'M12', revenue: 90000 }
];

const yearlyProjections = [
  { year: '2026', revenue: 240000, profit: 216480 },
  { year: '2027', revenue: 540000, profit: 478080 },
  { year: '2028', revenue: 1100000, profit: 940800 }
];

// Client Logos
const clientLogos = [
  { name: "Estée Lauder", logo: "https://upload.wikimedia.org/wikipedia/commons/7/79/Est%C3%A9e_Lauder_Companies_Logo.svg" },
  { name: "Clinique", logo: "https://upload.wikimedia.org/wikipedia/commons/4/45/Clinique_logo.svg" },
  { name: "JCPenney", logo: "https://upload.wikimedia.org/wikipedia/commons/6/61/JCPenney_logo_%282019%29.svg" },
  { name: "Macy's", logo: "https://upload.wikimedia.org/wikipedia/commons/a/a2/Macys_logo.svg" },
  { name: "Versace", logo: "https://upload.wikimedia.org/wikipedia/commons/4/4c/Versace_old_logo.svg" },
  { name: "TOUS", logo: "https://upload.wikimedia.org/wikipedia/commons/3/32/Tous_logo_2022.svg" }
];

const socialLinks = { instagram: "#", facebook: "https://www.facebook.com/graphicworldpr", linkedin: "#" };

// WhatsApp Button
const WhatsAppButton = () => {
  const { t } = useLanguage();
  return (
    <a href="https://wa.me/17875644035" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#128C7E] text-white p-4 rounded-full shadow-lg hover:scale-110 transition-all group" data-testid="whatsapp-button">
      <svg viewBox="0 0 24 24" className="w-7 h-7 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
      <span className="absolute right-full mr-3 bg-white text-gray-800 px-3 py-2 rounded-lg text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg">{t.common.chatWithUs}</span>
    </a>
  );
};

// Booking Modal
const CALENDLY_URL = "https://calendly.com/app/scheduling/meeting_types/user/me";

const BookingModal = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="card-surface p-8 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold flex items-center gap-2"><CalendarDays className="text-[#22D3EE]" />{t.booking.title}</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X size={24} /></button>
        </div>
        <div className="text-center py-6">
          <p className="text-zinc-400 mb-6">{t.booking.desc}</p>
          <div className="space-y-3">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 py-3 bg-[#22D3EE] text-black font-semibold hover:bg-[#22D3EE]/90 transition-colors" data-testid="calendly-link"><CalendarDays size={18} />{t.booking.schedule}</a>
            <a href="tel:+17875644035" className="flex items-center justify-center gap-2 py-3 border border-white/20 hover:border-white/40 transition-colors"><Phone size={18} />(787) 564-4035</a>
            <a href="mailto:graphicw1@gmail.com" className="flex items-center justify-center gap-2 py-3 border border-white/20 hover:border-white/40 transition-colors"><Mail size={18} />graphicw1@gmail.com</a>
          </div>
        </div>
        <p className="text-xs text-zinc-500 text-center">{t.booking.respondTime}</p>
      </div>
    </div>
  );
};

// Testimonials Carousel
const TestimonialsCarousel = () => {
  const { t, lang } = useLanguage();
  const [current, setCurrent] = useState(0);
  
  useEffect(() => {
    const timer = setInterval(() => setCurrent(c => (c + 1) % testimonials.length), 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="py-24 px-4 bg-[#0A0A0A]">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase">{t.testimonials.tag}</span>
          <h2 className="text-4xl font-bold mt-4">{t.testimonials.title}</h2>
          <p className="text-zinc-400 mt-4">{t.testimonials.subtitle}</p>
        </div>
        <div className="relative">
          <div className="card-surface p-8 md:p-12 text-center min-h-[300px] flex flex-col justify-center">
            <Quote className="w-12 h-12 mx-auto mb-6 text-[#D4AF37] opacity-50" />
            <p className="text-xl md:text-2xl text-zinc-200 mb-8 italic leading-relaxed">"{lang === 'es' ? testimonials[current].quoteEs : testimonials[current].quoteEn}"</p>
            <div>
              <div className="flex justify-center mb-2">{[...Array(testimonials[current].rating)].map((_, i) => <span key={i} className="text-[#D4AF37]">★</span>)}</div>
              <p className="font-bold text-lg">{lang === 'es' ? testimonials[current].nameEs : testimonials[current].nameEn}</p>
              <p className="text-[#D4AF37]">{testimonials[current].company}</p>
            </div>
          </div>
          <div className="flex justify-center gap-2 mt-6">
            {testimonials.map((_, i) => (
              <button key={i} onClick={() => setCurrent(i)} className={`w-3 h-3 rounded-full transition-all ${i === current ? 'bg-[#D4AF37] w-8' : 'bg-white/20 hover:bg-white/40'}`} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Language Toggle
const LanguageToggle = () => {
  const { lang, setLang } = useLanguage();
  return (
    <div className="flex items-center bg-white/5 rounded-full p-1" data-testid="language-toggle">
      <button onClick={() => setLang('en')} className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${lang === 'en' ? 'bg-white text-black' : 'text-zinc-400 hover:text-white'}`} data-testid="lang-en-btn">EN</button>
      <button onClick={() => setLang('es')} className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${lang === 'es' ? 'bg-white text-black' : 'text-zinc-400 hover:text-white'}`} data-testid="lang-es-btn">ES</button>
    </div>
  );
};

// Header
const Header = ({ currentView, setCurrentView }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleNav = (view) => {
    setCurrentView(view);
    setMobileMenuOpen(false);
    const routes = { landing: '/', graphicworks: '/graphic-works', rumbo: '/rumbo-group', investor: '/investor', resources: '/resources', admin: '/admin' };
    navigate(routes[view] || '/');
  };

  return (
    <>
      <header className="glass-header fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button onClick={() => handleNav('landing')} className="flex items-center space-x-2" data-testid="header-logo">
              <span className="text-xl font-bold tracking-tight">RUMBO</span>
              <span className="text-xs text-zinc-500">GROUP</span>
            </button>
            <nav className="hidden md:flex items-center space-x-1">
              <button onClick={() => handleNav('graphicworks')} className={`px-4 py-2 text-sm transition-colors ${currentView === 'graphicworks' ? 'text-[#D4AF37]' : 'text-zinc-400 hover:text-white'}`}>{t.nav.graphicWorks}</button>
              <span className="text-zinc-600">|</span>
              <button onClick={() => handleNav('rumbo')} className={`px-4 py-2 text-sm transition-colors ${currentView === 'rumbo' ? 'text-[#22D3EE]' : 'text-zinc-400 hover:text-white'}`}>{t.nav.rumboGroup}</button>
            </nav>
            <div className="hidden md:flex items-center space-x-3">
              <LanguageToggle />
              <button onClick={() => handleNav('resources')} className={`px-3 py-2 text-sm transition-colors ${currentView === 'resources' ? 'text-[#22D3EE]' : 'text-zinc-400 hover:text-white'}`} data-testid="nav-resources"><FolderOpen size={16} className="inline mr-1" />{t.nav.resources}</button>
              <button onClick={() => handleNav('investor')} className="px-3 py-2 text-sm text-zinc-400 hover:text-white transition-colors">{t.nav.investorPortal}</button>
              <button onClick={() => setBookingOpen(true)} className="px-3 py-2 text-sm text-[#22D3EE] hover:text-white transition-colors flex items-center gap-1"><CalendarDays size={16} />{t.nav.book}</button>
              <button onClick={() => handleNav('admin')} className="px-2 py-2 text-zinc-500 hover:text-white"><Lock size={16} /></button>
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2">{mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}</button>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-white/10">
              <nav className="flex flex-col space-y-2">
                <div className="px-4 py-2"><LanguageToggle /></div>
                <button onClick={() => handleNav('graphicworks')} className="px-4 py-2 text-left text-zinc-400 hover:text-white">{t.nav.graphicWorks}</button>
                <button onClick={() => handleNav('rumbo')} className="px-4 py-2 text-left text-zinc-400 hover:text-white">{t.nav.rumboGroup}</button>
                <button onClick={() => handleNav('resources')} className="px-4 py-2 text-left text-zinc-400 hover:text-white">{t.nav.resources}</button>
                <button onClick={() => handleNav('investor')} className="px-4 py-2 text-left text-zinc-400 hover:text-white">{t.nav.investorPortal}</button>
                <button onClick={() => setBookingOpen(true)} className="px-4 py-2 text-left text-[#22D3EE]"><CalendarDays size={16} className="inline mr-2" />{t.nav.book}</button>
              </nav>
            </div>
          )}
        </div>
      </header>
      <BookingModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
    </>
  );
};

// Landing Page
const LandingSelector = ({ setCurrentView }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const handleSelect = (view) => { setCurrentView(view); navigate(view === 'graphicworks' ? '/graphic-works' : '/rumbo-group'); };

  return (
    <div className="min-h-screen pt-16 grid-pattern">
      <div className="split-container">
        <div className="relative flex flex-col justify-center items-center p-8 lg:p-16 cursor-pointer group overflow-hidden" onClick={() => handleSelect('graphicworks')} data-testid="landing-graphic-works">
          <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="absolute inset-0" style={{ backgroundImage: `url(${IMAGES.heroGraphicWorks})`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.15 }} />
          <div className="relative z-10 text-center animate-slideInLeft">
            <span className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-6 block">{t.landing.est}</span>
            <h1 className="text-4xl lg:text-6xl font-black tracking-tighter mb-4">{t.landing.gwTitle}<br /><span className="gradient-text-gold">{t.landing.gwTitleAccent}</span></h1>
            <p className="text-zinc-400 text-lg mb-8 max-w-md">{t.landing.gwDesc}</p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">{t.landing.gwTags.map((tag, i) => <span key={i} className="px-3 py-1 text-xs border border-[#D4AF37]/30 text-[#D4AF37]">{tag}</span>)}</div>
            <button className="btn-gw px-8 py-3 border border-[#D4AF37] text-[#D4AF37] flex items-center mx-auto group-hover:bg-[#D4AF37] group-hover:text-black transition-all">{t.landing.gwCta} <ChevronRight className="ml-2" size={18} /></button>
          </div>
        </div>
        <div className="relative flex flex-col justify-center items-center p-8 lg:p-16 cursor-pointer group overflow-hidden border-l border-white/10" onClick={() => handleSelect('rumbo')} data-testid="landing-rumbo-group">
          <div className="absolute inset-0 bg-gradient-to-bl from-[#22D3EE]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="absolute inset-0" style={{ backgroundImage: `url(${IMAGES.heroRumboGroup})`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.15 }} />
          <div className="relative z-10 text-center animate-slideInRight">
            <span className="text-[#22D3EE] text-sm tracking-[0.3em] uppercase mb-6 block">{t.landing.aiPowered}</span>
            <h1 className="text-4xl lg:text-6xl font-black tracking-tighter mb-4">{t.landing.rumboTitle}<br /><span className="gradient-text-cyan">{t.landing.rumboTitleAccent}</span></h1>
            <p className="text-zinc-400 text-lg mb-8 max-w-md">{t.landing.rumboDesc}</p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">{t.landing.rumboTags.map((tag, i) => <span key={i} className="px-3 py-1 text-xs border border-[#22D3EE]/30 text-[#22D3EE]">{tag}</span>)}</div>
            <button className="btn-rumbo px-8 py-3 border border-[#22D3EE] text-[#22D3EE] flex items-center mx-auto group-hover:bg-[#22D3EE] group-hover:text-black transition-all">{t.landing.rumboCta} <ChevronRight className="ml-2" size={18} /></button>
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-6 text-center border-t border-white/10 bg-black/50 backdrop-blur-sm">
        <button onClick={() => { setCurrentView('investor'); navigate('/investor'); }} className="text-sm text-zinc-400 hover:text-white transition-colors flex items-center justify-center mx-auto"><Briefcase size={16} className="mr-2" />{t.landing.investorCta}<ArrowRight size={16} className="ml-2" /></button>
      </div>
    </div>
  );
};

// Graphic Works Page
const GraphicWorksPage = () => {
  const { t, lang } = useLanguage();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', service: '', message: '' });
  const [formStatus, setFormStatus] = useState('');
  const [bookingOpen, setBookingOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);

  const gwServices = [{ icon: Layers, key: 'pop' }, { icon: Building2, key: 'branding' }, { icon: Zap, key: 'channel' }, { icon: Megaphone, key: 'large' }, { icon: Settings, key: 'vehicle' }, { icon: PenTool, key: 'design' }];
  const categories = [{ key: 'all' }, { key: 'pop' }, { key: 'vehicle' }, { key: 'signage' }, { key: 'largeformat' }, { key: 'branding' }];
  const filteredItems = filter === 'all' ? portfolioItems : portfolioItems.filter(item => item.category === filter);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');
    try {
      await axios.post(`${API}/contact`, { ...formData, company_type: 'graphic_works' });
      setFormStatus('success');
      setFormData({ name: '', email: '', phone: '', company: '', service: '', message: '' });
    } catch (err) { setFormStatus('error'); }
  };

  return (
    <div className="min-h-screen pt-16">
      {/* Hero */}
      <section className="relative h-[80vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: `url(${IMAGES.heroGraphicWorks})`, backgroundSize: 'cover', backgroundPosition: 'center' }}><div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0A0A0A]" /></div>
        <div className="relative z-10 text-center px-4 max-w-4xl animate-fadeInUp">
          <span className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4 block">{t.gw.heroTag}</span>
          <h1 className="text-5xl sm:text-7xl font-black tracking-tighter mb-6">{t.gw.heroTitle}<br /><span className="gradient-text-gold">{t.gw.heroTitleAccent}</span></h1>
          <p className="text-xl text-zinc-300 mb-8 max-w-2xl mx-auto">{t.gw.heroDesc}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#quote" className="btn-gw px-8 py-4 bg-[#D4AF37] text-black font-semibold">{t.gw.getQuote}</a>
            <a href="#portfolio" className="px-8 py-4 border border-white/20 hover:border-white/40 transition-colors">{t.gw.viewPortfolio}</a>
            <button onClick={() => setBookingOpen(true)} className="px-8 py-4 border border-[#22D3EE]/50 text-[#22D3EE] hover:bg-[#22D3EE] hover:text-black transition-all flex items-center gap-2"><CalendarDays size={18} />{t.gw.bookConsultation}</button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 border-y border-white/10 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[{ value: "33+", key: 'years' }, { value: "2,500+", key: 'projects' }, { value: "150+", key: 'brands' }, { value: "100%", key: 'installation' }].map((stat, i) => (
            <div key={i} className="text-center"><div className="text-3xl font-black text-[#D4AF37]">{stat.value}</div><div className="text-sm text-zinc-500">{t.gw.stats[stat.key]}</div></div>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="py-24 px-4" id="services">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16"><span className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase">{t.gw.servicesTag}</span><h2 className="text-4xl font-bold mt-4">{t.gw.servicesTitle}</h2></div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {gwServices.map((service, i) => (
              <div key={i} className="card-surface p-8 hover:-translate-y-1 transition-all duration-300 group">
                <service.icon className="w-12 h-12 text-[#D4AF37] mb-6 group-hover:scale-110 transition-transform" />
                <h3 className="text-xl font-bold mb-3">{t.gw.services[service.key].title}</h3>
                <p className="text-zinc-400">{t.gw.services[service.key].desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Logos */}
      <section className="py-16 border-y border-white/10 overflow-hidden">
        <div className="mb-8 text-center"><span className="text-zinc-500 text-sm tracking-[0.2em] uppercase">{t.gw.clientsTag}</span></div>
        <div className="relative"><div className="flex logo-scroll">{[...clientLogos, ...clientLogos].map((client, i) => <div key={i} className="flex-shrink-0 mx-12 flex items-center justify-center h-16 w-40"><img src={client.logo} alt={client.name} className="max-h-10 max-w-full opacity-60 hover:opacity-100 transition-opacity filter brightness-0 invert" /></div>)}</div></div>
      </section>

      {/* Testimonials */}
      <TestimonialsCarousel />

      {/* Portfolio Gallery */}
      <section className="py-24 px-4 bg-[#141414]" id="portfolio">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12"><span className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase">{t.gw.portfolioTag}</span><h2 className="text-4xl font-bold mt-4">{t.gw.portfolioTitle}</h2><p className="text-zinc-400 mt-4">{t.gw.portfolioDesc}</p></div>
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {categories.map(cat => <button key={cat.key} onClick={() => setFilter(cat.key)} className={`px-4 py-2 text-sm transition-all ${filter === cat.key ? 'bg-[#D4AF37] text-black' : 'bg-white/5 text-zinc-400 hover:bg-white/10'}`}>{t.gw.portfolioCategories[cat.key]}</button>)}
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map(item => (
              <div key={item.id} className="group relative overflow-hidden cursor-pointer" onClick={() => setSelectedImage(item)}>
                <div className="aspect-[4/3] overflow-hidden"><img src={item.image} alt={lang === 'es' ? item.titleEs : item.titleEn} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" /></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-6"><h3 className="text-lg font-bold">{lang === 'es' ? item.titleEs : item.titleEn}</h3><p className="text-[#D4AF37] text-sm">{item.client}</p></div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {selectedImage && (
          <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}>
            <button className="absolute top-6 right-6 text-white hover:text-[#D4AF37]"><X size={32} /></button>
            <div className="max-w-5xl w-full" onClick={e => e.stopPropagation()}>
              <img src={selectedImage.image} alt="" className="w-full h-auto max-h-[80vh] object-contain" />
              <div className="mt-4 text-center"><h3 className="text-2xl font-bold">{lang === 'es' ? selectedImage.titleEs : selectedImage.titleEn}</h3><p className="text-[#D4AF37]">{selectedImage.client}</p></div>
            </div>
          </div>
        )}
      </section>

      {/* Quote Form */}
      <section className="py-24 px-4" id="quote">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12"><span className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase">{t.gw.quoteTag}</span><h2 className="text-4xl font-bold mt-4">{t.gw.quoteTitle}</h2><p className="text-zinc-400 mt-4">{t.gw.quoteDesc}</p></div>
          <form onSubmit={handleSubmit} className="card-surface p-8 space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <input type="text" placeholder={t.gw.form.name} required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-3 bg-transparent border border-white/10 focus:border-[#D4AF37]/50" />
              <input type="email" placeholder={t.gw.form.email} required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 bg-transparent border border-white/10 focus:border-[#D4AF37]/50" />
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <input type="tel" placeholder={t.gw.form.phone} value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-3 bg-transparent border border-white/10 focus:border-[#D4AF37]/50" />
              <input type="text" placeholder={t.gw.form.company} value={formData.company} onChange={e => setFormData({...formData, company: e.target.value})} className="w-full px-4 py-3 bg-transparent border border-white/10 focus:border-[#D4AF37]/50" />
            </div>
            <select value={formData.service} onChange={e => setFormData({...formData, service: e.target.value})} className="w-full px-4 py-3 bg-[#141414] border border-white/10">
              <option value="">{t.gw.form.service}</option>
              {Object.entries(t.gw.form.serviceOptions).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <textarea placeholder={t.gw.form.message} required rows={5} value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})} className="w-full px-4 py-3 bg-transparent border border-white/10 resize-none" />
            <button type="submit" disabled={formStatus === 'sending'} className="w-full btn-gw px-8 py-4 bg-[#D4AF37] text-black font-semibold disabled:opacity-50">{formStatus === 'sending' ? t.gw.form.sending : formStatus === 'success' ? t.gw.form.sent : t.gw.form.submit}</button>
            {formStatus === 'success' && <p className="text-green-500 text-center">{t.gw.form.success}</p>}
            {formStatus === 'error' && <p className="text-red-500 text-center">{t.gw.form.error}</p>}
          </form>
        </div>
      </section>

      {/* Google Maps */}
      <section className="py-16 px-4 border-t border-white/10">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="text-2xl font-bold mb-4">{t.gw.visitUs}</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-4"><Phone className="w-6 h-6 text-[#D4AF37]" /><a href="tel:+17875644035" className="hover:text-[#D4AF37]">(787) 564-4035</a></div>
              <div className="flex items-center space-x-4"><Mail className="w-6 h-6 text-[#D4AF37]" /><a href="mailto:graphicw1@gmail.com" className="hover:text-[#D4AF37]">graphicw1@gmail.com</a></div>
              <div className="flex items-center space-x-4"><MapPin className="w-6 h-6 text-[#D4AF37]" /><span>Carolina, Puerto Rico</span></div>
              <div className="flex items-center space-x-4"><Clock className="w-6 h-6 text-[#D4AF37]" /><span>{t.gw.hours}</span></div>
            </div>
          </div>
          <div className="h-80 rounded overflow-hidden">
            <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d121059!2d-66.0266!3d18.3827!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8c036f4c3c3c3c3d%3A0x3c3c3c3c3c3c3c3c!2sCarolina%2C%20Puerto%20Rico!5e0!3m2!1sen!2sus!4v1600000000000!5m2!1sen!2sus" width="100%" height="100%" style={{border:0}} allowFullScreen="" loading="lazy" title="Location" />
          </div>
        </div>
      </section>
      <BookingModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
    </div>
  );
};

// Rumbo Group Page
const RumboGroupPage = ({ setCurrentView }) => {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', interest: '', message: '' });
  const [formStatus, setFormStatus] = useState('');
  const [bookingOpen, setBookingOpen] = useState(false);

  const rumboServices = [{ icon: Globe, key: 'web' }, { icon: MessageSquare, key: 'social' }, { icon: Code, key: 'app' }, { icon: Target, key: 'ads' }, { icon: Bot, key: 'automation' }, { icon: Palette, key: 'brand' }, { icon: Mail, key: 'email' }, { icon: FileText, key: 'content' }];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');
    try {
      await axios.post(`${API}/contact`, { ...formData, company_type: 'rumbo_group' });
      setFormStatus('success');
      setFormData({ name: '', email: '', phone: '', company: '', interest: '', message: '' });
    } catch (err) { setFormStatus('error'); }
  };

  return (
    <div className="min-h-screen pt-16">
      {/* Hero */}
      <section className="relative h-[80vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: `url(${IMAGES.heroRumboGroup})`, backgroundSize: 'cover', backgroundPosition: 'center' }}><div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0A0A0A]" /></div>
        <div className="relative z-10 text-center px-4 max-w-4xl animate-fadeInUp">
          <img src={RUMBO_LOGO} alt="Rumbo Group" className="w-24 h-24 mx-auto mb-6 rounded-lg" />
          <span className="text-[#22D3EE] text-sm tracking-[0.3em] uppercase mb-4 block">{t.rumbo.heroTag}</span>
          <h1 className="text-5xl sm:text-7xl font-black tracking-tighter mb-6">{t.rumbo.heroTitle}<br /><span className="gradient-text-cyan">{t.rumbo.heroTitleAccent}</span></h1>
          <p className="text-xl text-zinc-300 mb-8 max-w-2xl mx-auto">{t.rumbo.heroDesc}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#pricing" className="btn-rumbo px-8 py-4 bg-[#22D3EE] text-black font-semibold">{t.rumbo.viewPricing}</a>
            <button onClick={() => { setCurrentView('investor'); navigate('/investor'); }} className="px-8 py-4 border border-white/20 hover:border-white/40">{t.rumbo.investorInfo}</button>
            <button onClick={() => setBookingOpen(true)} className="px-8 py-4 border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black flex items-center gap-2"><CalendarDays size={18} />{t.rumbo.bookConsultation}</button>
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-8 border-y border-white/10 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[{ icon: Bot, key: 'ai' }, { icon: Globe, key: 'bilingual' }, { icon: Percent, key: 'tax' }, { icon: Users, key: 'market' }].map((item, i) => (
            <div key={i} className="flex items-center justify-center space-x-3"><item.icon className="w-6 h-6 text-[#22D3EE]" /><span className="text-sm text-zinc-300">{t.rumbo.valueProps[item.key]}</span></div>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16"><span className="text-[#22D3EE] text-sm tracking-[0.3em] uppercase">{t.rumbo.servicesTag}</span><h2 className="text-4xl font-bold mt-4">{t.rumbo.servicesTitle}</h2><p className="text-zinc-400 mt-4 max-w-2xl mx-auto">{t.rumbo.servicesDesc}</p></div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {rumboServices.map((service, i) => (
              <div key={i} className="card-surface p-6 hover:-translate-y-1 transition-all group">
                <service.icon className="w-10 h-10 text-[#22D3EE] mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg font-bold mb-2">{t.rumbo.services[service.key].title}</h3>
                <p className="text-sm text-zinc-400">{t.rumbo.services[service.key].desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 px-4 bg-[#141414]" id="pricing">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16"><span className="text-[#22D3EE] text-sm tracking-[0.3em] uppercase">{t.rumbo.pricingTag}</span><h2 className="text-4xl font-bold mt-4">{t.rumbo.pricingTitle}</h2><p className="text-zinc-400 mt-4">{t.rumbo.pricingDesc}</p></div>
          <div className="grid lg:grid-cols-3 gap-8">
            {['basico', 'profesional', 'empresarial'].map((tier, i) => {
              const p = t.rumbo.pricing[tier];
              const isHL = tier === 'profesional';
              return (
                <div key={i} className={`relative p-8 border ${isHL ? 'border-[#22D3EE]/50 rumbo-glow' : 'border-white/10'} bg-[#0A0A0A]`}>
                  {p.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#22D3EE] text-black text-xs font-bold">{p.popular}</div>}
                  <h3 className="text-2xl font-bold mb-2">{p.name}</h3>
                  <div className="flex items-baseline mb-4"><span className="text-3xl font-black text-[#22D3EE]">{p.price}</span><span className="text-zinc-500 ml-1">{p.period}</span></div>
                  <p className="text-sm text-zinc-400 mb-6">{p.desc}</p>
                  <ul className="space-y-3 mb-8">{p.features.map((f, j) => <li key={j} className="flex items-start text-sm"><Check className="w-4 h-4 text-[#22D3EE] mr-2 mt-0.5" /><span className="text-zinc-300">{f}</span></li>)}</ul>
                  <a href="#contact" className={`block w-full py-3 text-center font-semibold ${isHL ? 'bg-[#22D3EE] text-black' : 'border border-white/20 hover:border-[#22D3EE]/50'}`}>{t.rumbo.getStarted}</a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Synergy */}
      <section className="py-24 px-4 relative overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: `url(${IMAGES.synergyBg})`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.1 }} />
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <span className="text-zinc-500 text-sm tracking-[0.3em] uppercase">{t.rumbo.synergyTag}</span>
          <h2 className="text-4xl font-bold mt-4 mb-6">{t.rumbo.synergyTitle}</h2>
          <p className="text-xl text-zinc-300 mb-8">{t.rumbo.synergyDesc}</p>
          <div className="grid md:grid-cols-2 gap-8 mt-12">
            <div className="card-surface p-8 border-l-4 border-[#D4AF37]"><h3 className="text-xl font-bold mb-3 text-[#D4AF37]">{t.rumbo.synergyGw.title}</h3><p className="text-zinc-400">{t.rumbo.synergyGw.desc}</p></div>
            <div className="card-surface p-8 border-l-4 border-[#22D3EE]"><h3 className="text-xl font-bold mb-3 text-[#22D3EE]">{t.rumbo.synergyRumbo.title}</h3><p className="text-zinc-400">{t.rumbo.synergyRumbo.desc}</p></div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-24 px-4 bg-[#141414]" id="contact">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12"><span className="text-[#22D3EE] text-sm tracking-[0.3em] uppercase">{t.rumbo.contactTag}</span><h2 className="text-4xl font-bold mt-4">{t.rumbo.contactTitle}</h2><p className="text-zinc-400 mt-4">{t.rumbo.contactDesc}</p></div>
          <form onSubmit={handleSubmit} className="card-surface p-8 space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <input type="text" placeholder={t.gw.form.name} required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-3 bg-transparent border border-white/10" />
              <input type="email" placeholder={t.gw.form.email} required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 bg-transparent border border-white/10" />
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <input type="tel" placeholder={t.gw.form.phone} value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-3 bg-transparent border border-white/10" />
              <input type="text" placeholder={t.gw.form.company} value={formData.company} onChange={e => setFormData({...formData, company: e.target.value})} className="w-full px-4 py-3 bg-transparent border border-white/10" />
            </div>
            <select value={formData.interest} onChange={e => setFormData({...formData, interest: e.target.value})} className="w-full px-4 py-3 bg-[#141414] border border-white/10">
              <option value="">{t.rumbo.form.interest}</option>
              {Object.entries(t.rumbo.form.interestOptions).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <textarea placeholder={t.rumbo.form.message} required rows={5} value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})} className="w-full px-4 py-3 bg-transparent border border-white/10 resize-none" />
            <button type="submit" disabled={formStatus === 'sending'} className="w-full btn-rumbo px-8 py-4 bg-[#22D3EE] text-black font-semibold disabled:opacity-50">{formStatus === 'sending' ? t.gw.form.sending : formStatus === 'success' ? t.gw.form.sent : t.rumbo.form.submit}</button>
            {formStatus === 'success' && <p className="text-green-500 text-center">{t.gw.form.success}</p>}
            {formStatus === 'error' && <p className="text-red-500 text-center">{t.gw.form.error}</p>}
          </form>
        </div>
      </section>
      <BookingModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
    </div>
  );
};

// Marketing Resource Center
const ResourceCenter = () => {
  const { t, lang } = useLanguage();
  const [copied, setCopied] = useState(null);
  const [previewEmail, setPreviewEmail] = useState(null);

  const copyToClipboard = (id, text) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="min-h-screen pt-16">
      {/* Hero */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#22D3EE]/5 to-transparent" />
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <span className="text-[#22D3EE] text-sm tracking-[0.3em] uppercase">{t.resources.heroTag}</span>
          <h1 className="text-5xl font-black tracking-tighter mt-4">{t.resources.heroTitle}<br /><span className="gradient-text-cyan">{t.resources.heroTitleAccent}</span></h1>
          <p className="text-xl text-zinc-400 mt-6">{t.resources.heroDesc}</p>
        </div>
      </section>

      {/* Business Documents */}
      <section className="py-16 px-4 bg-[#141414]">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-8"><FileText className="w-8 h-8 text-[#22D3EE]" /><div><h2 className="text-2xl font-bold">{t.resources.sections.business.title}</h2><p className="text-zinc-500">{t.resources.sections.business.desc}</p></div></div>
          <div className="grid md:grid-cols-2 gap-4">
            {marketingResources.business.map(doc => (
              <a key={doc.id} href={doc.url} target="_blank" rel="noopener noreferrer" className="card-surface p-6 flex items-center justify-between hover:border-[#22D3EE]/30 group">
                <div className="flex items-center gap-4"><FileText className="w-10 h-10 text-zinc-500" /><div><div className="font-semibold group-hover:text-[#22D3EE]">{lang === 'es' ? doc.nameEs : doc.nameEn}</div><div className="text-sm text-zinc-500">{doc.type} · {doc.size}</div></div></div>
                <Download className="w-5 h-5 text-zinc-500 group-hover:text-[#22D3EE]" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Branding Assets */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-8"><FileImage className="w-8 h-8 text-[#D4AF37]" /><div><h2 className="text-2xl font-bold">{t.resources.sections.branding.title}</h2><p className="text-zinc-500">{t.resources.sections.branding.desc}</p></div></div>
          <div className="grid md:grid-cols-2 gap-4">
            {marketingResources.branding.map(doc => (
              <a key={doc.id} href={doc.url} target="_blank" rel="noopener noreferrer" className="card-surface p-6 flex items-center justify-between hover:border-[#D4AF37]/30 group">
                <div className="flex items-center gap-4">{doc.type === 'JPG' ? <img src={doc.url} alt="" className="w-12 h-12 rounded object-cover" /> : <FileImage className="w-10 h-10 text-zinc-500" />}<div><div className="font-semibold group-hover:text-[#D4AF37]">{lang === 'es' ? doc.nameEs : doc.nameEn}</div><div className="text-sm text-zinc-500">{doc.type} · {doc.size}</div></div></div>
                <Download className="w-5 h-5 text-zinc-500 group-hover:text-[#D4AF37]" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* One-Pagers */}
      <section className="py-16 px-4 bg-[#141414]">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-8"><Presentation className="w-8 h-8 text-[#22D3EE]" /><div><h2 className="text-2xl font-bold">{t.resources.sections.onepagers.title}</h2><p className="text-zinc-500">{t.resources.sections.onepagers.desc}</p></div></div>
          <div className="grid md:grid-cols-3 gap-4">
            {marketingResources.onepagers.map(doc => (
              <div key={doc.id} className="card-surface p-6">
                <div className="flex items-center gap-3 mb-4"><FileText className="w-8 h-8 text-[#22D3EE]" /><span className="text-xs bg-[#22D3EE]/20 text-[#22D3EE] px-2 py-1 rounded">{doc.type}</span></div>
                <h3 className="font-semibold mb-2">{lang === 'es' ? doc.nameEs : doc.nameEn}</h3>
                <p className="text-sm text-zinc-500 mb-4">{lang === 'es' ? doc.descEs : doc.descEn}</p>
                <button className="w-full py-2 text-sm border border-white/20 hover:border-[#22D3EE]/50 hover:text-[#22D3EE]">{t.resources.preview}</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Email Templates */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-8"><MailIcon className="w-8 h-8 text-[#D4AF37]" /><div><h2 className="text-2xl font-bold">{t.resources.sections.email.title}</h2><p className="text-zinc-500">{t.resources.sections.email.desc}</p></div></div>
          <div className="grid md:grid-cols-2 gap-4">
            {marketingResources.email.map(doc => (
              <div key={doc.id} className="card-surface p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3"><MailIcon className="w-6 h-6 text-[#D4AF37]" /><h3 className="font-semibold">{lang === 'es' ? doc.nameEs : doc.nameEn}</h3></div>
                  <span className="text-xs bg-[#D4AF37]/20 text-[#D4AF37] px-2 py-1 rounded">{doc.type}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setPreviewEmail(doc.id)} className="flex-1 py-2 text-sm border border-white/20 hover:border-[#D4AF37]/50 hover:text-[#D4AF37]">{t.resources.preview}</button>
                  <button onClick={() => copyToClipboard(doc.id, emailTemplates[doc.id]?.[lang]?.body || '')} className="flex-1 py-2 text-sm bg-[#D4AF37] text-black font-medium hover:bg-[#D4AF37]/90 flex items-center justify-center gap-2">
                    {copied === doc.id ? <><Check size={16} />{t.resources.copied}</> : <><Copy size={16} />{t.resources.copyEmail}</>}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Email Preview Modal */}
      {previewEmail && emailTemplates[previewEmail] && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setPreviewEmail(null)}>
          <div className="card-surface p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">{lang === 'es' ? marketingResources.email.find(e => e.id === previewEmail)?.nameEs : marketingResources.email.find(e => e.id === previewEmail)?.nameEn}</h2>
              <button onClick={() => setPreviewEmail(null)} className="text-zinc-400 hover:text-white"><X size={24} /></button>
            </div>
            <div className="mb-4"><div className="text-xs text-zinc-500 uppercase mb-1">Subject</div><p className="font-medium">{emailTemplates[previewEmail][lang].subject}</p></div>
            <div><div className="text-xs text-zinc-500 uppercase mb-1">Body</div><pre className="bg-white/5 p-4 rounded text-sm whitespace-pre-wrap font-sans">{emailTemplates[previewEmail][lang].body}</pre></div>
            <div className="flex gap-2 mt-6">
              <button onClick={() => copyToClipboard(previewEmail + '-preview', emailTemplates[previewEmail][lang].body)} className="flex-1 py-3 bg-[#D4AF37] text-black font-semibold flex items-center justify-center gap-2">
                {copied === previewEmail + '-preview' ? <><Check size={18} />{t.resources.copied}</> : <><Copy size={18} />{t.resources.copyEmail}</>}
              </button>
              <button onClick={() => setPreviewEmail(null)} className="flex-1 py-3 border border-white/20">{t.admin.detail.close}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Investor Portal
const InvestorPortal = () => {
  const { t, lang } = useLanguage();
  return (
    <div className="min-h-screen pt-16">
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: `url(${IMAGES.investorBg})`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.1 }} />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16 animate-fadeInUp">
            <span className="text-zinc-500 text-sm tracking-[0.3em] uppercase">{t.investor.heroTag}</span>
            <h1 className="text-5xl sm:text-6xl font-black tracking-tighter mt-4">{t.investor.heroTitle}<br /><span className="text-white">{t.investor.heroTitleAccent}</span></h1>
            <p className="text-xl text-zinc-400 mt-6 max-w-2xl mx-auto">{t.investor.heroDesc}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[{ icon: TrendingUp, ...t.investor.metrics.revenue, color: "#22D3EE" }, { icon: Percent, ...t.investor.metrics.tax, color: "#D4AF37" }, { icon: Users, ...t.investor.metrics.clients, color: "#22D3EE" }, { icon: Award, ...t.investor.metrics.experience, color: "#D4AF37" }].map((m, i) => (
              <div key={i} className="card-surface p-6 text-center"><m.icon className="w-8 h-8 mx-auto mb-4" style={{color: m.color}} /><div className="text-3xl font-black" style={{color: m.color}}>{m.value}</div><div className="text-sm text-zinc-500 mt-1">{m.label}</div></div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-[#141414]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12"><h2 className="text-3xl font-bold">{t.investor.projections.title}</h2><p className="text-zinc-400 mt-2">{t.investor.projections.subtitle}</p></div>
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueData}><defs><linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#22D3EE" stopOpacity={0.3}/><stop offset="95%" stopColor="#22D3EE" stopOpacity={0}/></linearGradient></defs><CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" /><XAxis dataKey="month" stroke="#71717A" /><YAxis stroke="#71717A" tickFormatter={v => `$${v/1000}k`} /><Tooltip contentStyle={{backgroundColor:'#141414',border:'1px solid rgba(255,255,255,0.1)'}} formatter={v => [`$${v.toLocaleString()}`, t.investor.projections.revenue]} /><Area type="monotone" dataKey="revenue" stroke="#22D3EE" fill="url(#revenueGradient)" strokeWidth={2} /></AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {yearlyProjections.map((y, i) => (
              <div key={i} className="card-surface p-6"><div className="text-sm text-zinc-500 mb-2">{y.year}</div><div className="text-2xl font-bold text-[#22D3EE]">${(y.revenue/1000000).toFixed(2)}M</div><div className="text-sm text-zinc-400">{t.investor.projections.revenue}</div><div className="mt-4 pt-4 border-t border-white/10"><div className="text-lg font-bold text-green-500">${(y.profit/1000000).toFixed(2)}M</div><div className="text-sm text-zinc-400">{t.investor.projections.netAfterTax}</div></div></div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="card-surface p-8 md:p-12 border-l-4 border-[#D4AF37]">
            <div className="flex items-start gap-6"><Shield className="w-12 h-12 text-[#D4AF37] flex-shrink-0" /><div><h3 className="text-2xl font-bold mb-4">{t.investor.act60.title}</h3><p className="text-zinc-400 mb-6">{t.investor.act60.desc}</p><div className="grid sm:grid-cols-3 gap-6"><div><div className="text-2xl font-bold text-[#D4AF37]">4%</div><div className="text-sm text-zinc-500">{t.investor.act60.corpTax}</div></div><div><div className="text-2xl font-bold text-[#D4AF37]">0%</div><div className="text-sm text-zinc-500">{t.investor.act60.divTax}</div></div><div><div className="text-2xl font-bold text-[#D4AF37]">20 yrs</div><div className="text-sm text-zinc-500">{t.investor.act60.duration}</div></div></div></div></div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-[#141414]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12"><h2 className="text-3xl font-bold">{t.investor.advantages.title}</h2><p className="text-zinc-400 mt-2">{t.investor.advantages.subtitle}</p></div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{t.investor.advantages.items.map((a, i) => <div key={i} className="card-surface p-6"><h4 className="font-bold mb-2">{a.title}</h4><p className="text-sm text-zinc-400">{a.desc}</p></div>)}</div>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12"><h2 className="text-3xl font-bold">{t.investor.downloads.title}</h2><p className="text-zinc-400 mt-2">{t.investor.downloads.subtitle}</p></div>
          <div className="space-y-4">
            {[...marketingResources.business, ...marketingResources.branding].map(doc => (
              <a key={doc.id} href={doc.url} target="_blank" rel="noopener noreferrer" className="card-surface p-6 flex items-center justify-between hover:border-white/30 group">
                <div className="flex items-center gap-4"><FileText className="w-8 h-8 text-zinc-500" /><div><div className="font-semibold group-hover:text-[#22D3EE]">{lang === 'es' ? doc.nameEs : doc.nameEn}</div><div className="text-sm text-zinc-500">{doc.type} · {doc.size}</div></div></div>
                <Download className="w-5 h-5 text-zinc-500 group-hover:text-[#22D3EE]" />
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4 bg-[#141414] border-t border-white/10">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">{t.investor.cta.title}</h2>
          <p className="text-xl text-zinc-400 mb-8">{t.investor.cta.desc}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="mailto:graphicw1@gmail.com?subject=Investor Inquiry" className="btn-rumbo px-8 py-4 bg-[#22D3EE] text-black font-semibold flex items-center"><Mail className="mr-2" size={18} />{t.investor.cta.contactUs}</a>
            <a href="tel:+17875644035" className="px-8 py-4 border border-white/20 hover:border-white/40 flex items-center"><Phone className="mr-2" size={18} />{t.investor.cta.call}</a>
          </div>
        </div>
      </section>
    </div>
  );
};

// Admin Dashboard (simplified for space)
const AdminDashboard = () => {
  const { t } = useLanguage();
  const [isAuth, setIsAuth] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [notes, setNotes] = useState('');

  const handleLogin = async (e) => { e.preventDefault(); try { await axios.post(`${API}/admin/login`, { password }); setIsAuth(true); fetchData(); } catch { setError(t.admin.login.error); } };
  const fetchData = async () => { setLoading(true); try { const [c, s] = await Promise.all([axios.get(`${API}/contacts`), axios.get(`${API}/stats`)]); setContacts(c.data); setStats(s.data); } catch {} setLoading(false); };
  const updateStatus = async (id, status) => { try { await axios.patch(`${API}/contacts/${id}`, { status }); fetchData(); if (selected?.id === id) setSelected({...selected, status}); } catch {} };
  const deleteContact = async (id) => { if (window.confirm('Delete?')) { try { await axios.delete(`${API}/contacts/${id}`); setSelected(null); fetchData(); } catch {} } };

  const filteredContacts = contacts.filter(c => (filterType === 'all' || c.company_type === filterType) && (filterStatus === 'all' || c.status === filterStatus));
  const statusColors = { new: 'bg-blue-500/20 text-blue-400', contacted: 'bg-yellow-500/20 text-yellow-400', converted: 'bg-green-500/20 text-green-400', archived: 'bg-zinc-500/20 text-zinc-400' };

  if (!isAuth) return (
    <div className="min-h-screen pt-16 flex items-center justify-center px-4">
      <div className="card-surface p-8 w-full max-w-md">
        <div className="text-center mb-8"><Lock className="w-12 h-12 mx-auto mb-4 text-zinc-500" /><h1 className="text-2xl font-bold">{t.admin.login.title}</h1></div>
        <form onSubmit={handleLogin} className="space-y-4">
          <input type="password" placeholder={t.admin.login.password} value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-3 bg-transparent border border-white/10" data-testid="admin-password-input" />
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <button type="submit" className="w-full py-3 bg-white text-black font-semibold">{t.admin.login.submit}</button>
        </form>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pt-16 px-4 pb-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8 pt-8"><h1 className="text-3xl font-bold">{t.admin.title}</h1><button onClick={fetchData} className="p-2 text-zinc-400 hover:text-white"><RefreshCw size={20} className={loading ? 'animate-spin' : ''} /></button></div>
        {stats && <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">{[{v:stats.total_contacts,l:t.admin.stats.total,c:'white'},{v:stats.new_contacts,l:t.admin.stats.new,c:'blue-400'},{v:stats.contacted,l:t.admin.stats.contacted,c:'yellow-400'},{v:stats.converted,l:t.admin.stats.converted,c:'green-400'},{v:stats.total_downloads,l:t.admin.stats.downloads,c:'zinc-400'}].map((s,i)=><div key={i} className="card-surface p-4 text-center"><div className={`text-2xl font-bold text-${s.c}`}>{s.v}</div><div className="text-xs text-zinc-500">{s.l}</div></div>)}</div>}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex items-center gap-2"><Filter size={16} className="text-zinc-500" />
            <select value={filterType} onChange={e=>setFilterType(e.target.value)} className="bg-[#141414] border border-white/10 px-3 py-2 text-sm"><option value="all">{t.admin.filters.all}</option><option value="graphic_works">{t.admin.filters.graphicWorks}</option><option value="rumbo_group">{t.admin.filters.rumboGroup}</option></select>
            <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} className="bg-[#141414] border border-white/10 px-3 py-2 text-sm"><option value="all">{t.admin.filters.statusAll}</option><option value="new">{t.admin.filters.new}</option><option value="contacted">{t.admin.filters.contacted}</option><option value="converted">{t.admin.filters.converted}</option><option value="archived">{t.admin.filters.archived}</option></select>
          </div>
        </div>
        <div className="card-surface overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5"><tr>{['date','name','email','company','type','status','actions'].map(h=><th key={h} className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">{t.admin.table[h]}</th>)}</tr></thead>
            <tbody className="divide-y divide-white/5">
              {filteredContacts.length === 0 ? <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-500">{t.admin.noContacts}</td></tr> :
                filteredContacts.map(c=><tr key={c.id} className="hover:bg-white/5"><td className="px-4 py-3 text-sm text-zinc-400">{new Date(c.timestamp).toLocaleDateString()}</td><td className="px-4 py-3 text-sm font-medium">{c.name}</td><td className="px-4 py-3 text-sm text-zinc-400">{c.email}</td><td className="px-4 py-3 text-sm text-zinc-400">{c.company||'-'}</td><td className="px-4 py-3"><span className={`px-2 py-1 text-xs rounded ${c.company_type==='graphic_works'?'bg-[#D4AF37]/20 text-[#D4AF37]':'bg-[#22D3EE]/20 text-[#22D3EE]'}`}>{c.company_type==='graphic_works'?'GW':'RG'}</span></td><td className="px-4 py-3"><span className={`px-2 py-1 text-xs rounded ${statusColors[c.status]||statusColors.new}`}>{c.status}</span></td><td className="px-4 py-3"><button onClick={()=>{setSelected(c);setNotes(c.notes||'');}} className="p-1 text-zinc-400 hover:text-white"><Eye size={16}/></button></td></tr>)
              }
            </tbody>
          </table>
        </div>
        {selected && <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={()=>setSelected(null)}><div className="card-surface p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()}><div className="flex items-center justify-between mb-6"><h2 className="text-xl font-bold">{selected.name}</h2><button onClick={()=>setSelected(null)}><X size={24}/></button></div><div className="space-y-4"><div><div className="text-xs text-zinc-500 uppercase mb-1">{t.common.email}</div><a href={`mailto:${selected.email}`} className="text-[#22D3EE]">{selected.email}</a></div><div><div className="text-xs text-zinc-500 uppercase mb-1">{t.admin.detail.phone}</div><p>{selected.phone||'-'}</p></div><div><div className="text-xs text-zinc-500 uppercase mb-1">{t.admin.detail.message}</div><p className="bg-white/5 p-3 rounded text-sm">{selected.message}</p></div><div><div className="text-xs text-zinc-500 uppercase mb-2">{t.admin.detail.updateStatus}</div><div className="flex flex-wrap gap-2">{['new','contacted','converted','archived'].map(s=><button key={s} onClick={()=>updateStatus(selected.id,s)} className={`px-3 py-1 text-xs rounded ${selected.status===s?statusColors[s]:'bg-white/5 text-zinc-400 hover:bg-white/10'}`}>{s}</button>)}</div></div></div><div className="flex gap-4 mt-6 pt-6 border-t border-white/10"><button onClick={()=>deleteContact(selected.id)} className="flex-1 py-2 bg-red-500/20 text-red-400 flex items-center justify-center gap-2"><Trash2 size={16}/>{t.admin.detail.delete}</button><button onClick={()=>setSelected(null)} className="flex-1 py-2 bg-white/10">{t.admin.detail.close}</button></div></div></div>}
      </div>
    </div>
  );
};

// Footer
const Footer = () => {
  const { t } = useLanguage();
  return (
    <footer className="py-12 px-4 border-t border-white/10">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          <div><h4 className="font-bold mb-4">Rumbo Group LLC</h4><p className="text-sm text-zinc-500 mb-4">{t.footer.rumboDesc}</p><div className="flex gap-4"><a href={socialLinks.instagram} className="text-zinc-500 hover:text-[#E4405F]"><Instagram size={20}/></a><a href={socialLinks.facebook} className="text-zinc-500 hover:text-[#1877F2]"><Facebook size={20}/></a><a href={socialLinks.linkedin} className="text-zinc-500 hover:text-[#0A66C2]"><Linkedin size={20}/></a></div></div>
          <div><h4 className="font-bold mb-4 text-[#D4AF37]">Graphic Works</h4><ul className="space-y-2 text-sm text-zinc-500"><li><a href="/graphic-works#services" className="hover:text-white">{t.footer.services}</a></li><li><a href="/graphic-works#portfolio" className="hover:text-white">{t.footer.portfolio}</a></li><li><a href="/graphic-works#quote" className="hover:text-white">{t.footer.getQuote}</a></li></ul></div>
          <div><h4 className="font-bold mb-4 text-[#22D3EE]">Rumbo Group</h4><ul className="space-y-2 text-sm text-zinc-500"><li><a href="/rumbo-group#services" className="hover:text-white">{t.footer.aiServices}</a></li><li><a href="/rumbo-group#pricing" className="hover:text-white">{t.footer.pricing}</a></li><li><a href="/rumbo-group#contact" className="hover:text-white">{t.footer.contact}</a></li></ul></div>
          <div><h4 className="font-bold mb-4">{t.footer.contact}</h4><ul className="space-y-2 text-sm text-zinc-500"><li><a href="tel:+17875644035">(787) 564-4035</a></li><li><a href="mailto:graphicw1@gmail.com">graphicw1@gmail.com</a></li><li>Carolina, Puerto Rico</li></ul></div>
        </div>
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4"><div className="text-sm text-zinc-500">© {new Date().getFullYear()} Rumbo Group LLC. {t.footer.rights}</div><a href="https://www.gworkspr2.com" target="_blank" rel="noopener noreferrer" className="text-sm text-zinc-500 hover:text-white flex items-center">gworkspr2.com <ExternalLink size={14} className="ml-1"/></a></div>
      </div>
    </footer>
  );
};

// Main App
function App() {
  const [currentView, setCurrentView] = useState('landing');
  const [lang, setLang] = useState('en');
  const t = translations[lang];

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      <BrowserRouter>
        <div className="App min-h-screen bg-[#0A0A0A] text-white">
          <Header currentView={currentView} setCurrentView={setCurrentView} />
          <Routes>
            <Route path="/" element={<LandingSelector setCurrentView={setCurrentView} />} />
            <Route path="/graphic-works" element={<GraphicWorksPage />} />
            <Route path="/rumbo-group" element={<RumboGroupPage setCurrentView={setCurrentView} />} />
            <Route path="/investor" element={<InvestorPortal />} />
            <Route path="/resources" element={<ResourceCenter />} />
            <Route path="/admin" element={<AdminDashboard />} />
          </Routes>
          <Footer />
          <WhatsAppButton />
        </div>
      </BrowserRouter>
    </LanguageContext.Provider>
  );
}

export default App;
